// PasswordDialog.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    InputAdornment
} from "@material-ui/core";
import { Alert } from '@mui/material';
import { makeStyles } from "@material-ui/core/styles";
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import AcceptRequestService from '../../services/AcceptRequestService';
import ContractService from '../../services/ContractService';

const useStyles = makeStyles((theme) => ({
    passwordInput: {
        '& input': {
            textAlign: 'center',
            fontSize: '20px',
            letterSpacing: '2px'
        }
    },
    timer: {
        textAlign: 'center',
        marginTop: theme.spacing(2),
        color: theme.palette.warning.main,
        fontWeight: 'bold'
    }
}));

export default function PasswordDialog({ 
    open, 
    onClose, 
    onSuccess, 
    personId,
    personName,
    title = "Accès sécurisé aux documents",
    typecontrat = "Personnel", 
    lienapresvalidate = "/contract_user"  
}) {
    const classes = useStyles();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [requestLoading, setRequestLoading] = useState(false);
    const [pinCode, setPinCode] = useState('');
    const [timer, setTimer] = useState(0);
    const [step, setStep] = useState('password');
    const [codeSent, setCodeSent] = useState(false);
    let intervalRef = null;

    useEffect(() => {
        return () => {
            if (intervalRef) clearInterval(intervalRef);
        };
    }, []);

    const startTimer = (durationInSeconds) => {
        if (intervalRef) clearInterval(intervalRef);
        
        setTimer(durationInSeconds);
        intervalRef = setInterval(() => {
            setTimer((prevTimer) => {
                if (prevTimer <= 1) {
                    clearInterval(intervalRef);
                    return 0;
                }
                return prevTimer - 1;
            });
        }, 1000);
    };

    const handleRequestCode = async () => {
        setRequestLoading(true);
        setError('');

        try {
            // Utiliser le typecontrat passé en prop
            const result = await ContractService.requestaccess(typecontrat);

            if (result && result.status) {
                setCodeSent(true);
                setStep('pin');
                startTimer(result.expires_in || 3600);
            } else {
                setError(result?.message || "Erreur lors de l'envoi du code");
            }
        } catch (err) {
            setError(err?.message || "Erreur lors de l'envoi du code");
            console.error(err);
        } finally {
            setRequestLoading(false);
        }
    };

    const handleVerifyPassword = async () => {
        setLoading(true);
        setError("");

        if (password.trim() === "") {
            setError("Mot de passe requis !!");
            setLoading(false);
            return;
        }

        try {
            const result = await AcceptRequestService.verifyMdp(password, 2);

            if (result.status) {
                await handleRequestCode();
            } else {
                setError(result.message || "Mot de passe incorrect");
            }
        } catch (err) {
            setError("Une erreur s'est produite");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyPin = async () => {
        if (pinCode.length !== 6) {
            setError("Veuillez saisir un code PIN à 6 chiffres");
            return;
        }

        if (timer === 0 && codeSent) {
            setError("Le code a expiré. Veuillez en demander un nouveau.");
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Utiliser le typecontrat passé en prop
            const result = await ContractService.verifyandview(typecontrat, pinCode);

            if (result && result.status) {
                // Stocker la session avec le bon token basé sur le typecontrat
                const tokenKey = typecontrat === "Personnel" ? 'personnel_access_token' : 'stagiaire_access_token';
                const expiryKey = typecontrat === "Personnel" ? 'personnel_access_expiry' : 'stagiaire_access_expiry';
                
                const accessToken = result.access_token || `${typecontrat.toLowerCase()}_access_${Date.now()}`;
                const expiryTime = Date.now() + (3600 * 1000);
                sessionStorage.setItem(tokenKey, accessToken);
                sessionStorage.setItem(expiryKey, expiryTime.toString());
                
                handleClose();
                
               if (personId) {
                    navigate(`${lienapresvalidate}/${personId}`);
                } else {
                    navigate(lienapresvalidate);
                }
            } else {
                setError(result?.message || 'Code PIN incorrect ou expiré');
            }
        } catch (err) {
            setError('Erreur lors de la vérification du code');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (timer > 0) {
            if (!window.confirm("Un code valide existe déjà. Voulez-vous vraiment en demander un nouveau ?")) {
                return;
            }
        }
        
        setRequestLoading(true);
        setError('');
        setPinCode('');
        
        try {
            // Utiliser le typecontrat passé en prop
            const result = await ContractService.requestaccess(typecontrat);

            if (result && result.status) {
                setCodeSent(true);
                startTimer(result.expires_in || 3600);
                setError('');
            } else {
                setError(result?.message || "Erreur lors de l'envoi du nouveau code");
            }
        } catch (err) {
            setError(err?.message || "Erreur lors de l'envoi du nouveau code");
            console.error(err);
        } finally {
            setRequestLoading(false);
        }
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleFermer = () => {
        handleClose();
        navigate('/home');
    };

    const handleClose = () => {
        if (intervalRef) clearInterval(intervalRef);
        setPassword('');
        setPinCode('');
        setError('');
        setStep('password');
        setCodeSent(false);
        setTimer(0);
        setLoading(false);
        setRequestLoading(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                🔒 {title}
                {personName && (
                    <Typography variant="subtitle2" color="textSecondary">
                        Document de: {personName}
                    </Typography>
                )}
                <Typography variant="subtitle2" color="textSecondary">
                    {step === 'password' 
                        ? 'Veuillez saisir votre mot de passe'
                        : 'Veuillez saisir le code PIN reçu par email (valable 1 heure)'
                    }
                </Typography>
            </DialogTitle>
            <DialogContent>
                {step === 'password' ? (
                    <Box textAlign="center" py={2}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Mot de passe"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            margin="normal"
                            autoFocus
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && password) {
                                    handleVerifyPassword();
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        {error && <Alert severity="error" style={{ marginTop: 16 }}>{error}</Alert>}
                        
                        <Box mt={4}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleVerifyPassword}
                                disabled={loading || !password}
                                fullWidth
                            >
                                {loading ? <CircularProgress size={24} /> : 'Continuer'}
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box py={2}>
                        <Alert severity="info" style={{ marginBottom: 16 }}>
                            Un code PIN à 6 chiffres a été envoyé à votre adresse email pour le type: <strong>{typecontrat}</strong>
                            <br />
                            <strong>Valable 1 heure - Usage unique</strong>
                        </Alert>
                        
                        {timer > 0 && (
                            <Typography className={classes.timer}>
                                ⏱️ Code valable encore : {formatTime(timer)}
                            </Typography>
                        )}
                        
                        {timer === 0 && codeSent && (
                            <Alert severity="warning" style={{ marginBottom: 16 }}>
                                Le code a expiré. Veuillez en demander un nouveau.
                            </Alert>
                        )}
                        
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Code PIN"
                            value={pinCode}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                setPinCode(value);
                            }}
                            placeholder="000000"
                            margin="normal"
                            autoFocus
                            inputProps={{ 
                                maxLength: 6, 
                                pattern: '[0-9]*',
                                style: { textAlign: 'center', fontSize: '24px', letterSpacing: '4px' }
                            }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && pinCode.length === 6 && timer > 0) {
                                    handleVerifyPin();
                                }
                            }}
                        />
                        
                        {error && <Alert severity="error" style={{ marginTop: 16 }}>{error}</Alert>}
                        
                        <Box mt={4} display="flex" gap={2}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleResendCode}
                                disabled={requestLoading}
                                fullWidth
                            >
                                {requestLoading ? <CircularProgress size={24} /> : 'Renvoyer le code'}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleVerifyPin}
                                disabled={loading || pinCode.length !== 6 || timer === 0}
                                fullWidth
                            >
                                {loading ? <CircularProgress size={24} /> : 'Voir le contrat'}
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleFermer} color="secondary">
                    Annuler
                </Button>
            </DialogActions>
        </Dialog>
    );
}